// src/components/PrivateRoute/PrivateRoute.jsx
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import api, { setToken } from '../../services/api';
import './style.scss';

const TIPOS = new Set(['professor', 'instituicao']);

function resolveDashboardPath(tipo) {
  if (tipo === 'professor') return '/dashboard/professor';
  if (tipo === 'instituicao') return '/dashboard/instituicao';
  return null;
}

export default function PrivateRoute() {
  const location = useLocation();

  const [state, setState] = useState({
    checking: true,
    allowed: false,
    redirectTo: null,
  });

  const currentPath = useMemo(() => location?.pathname || '/', [location]);

  // Escuta o evento emitido pelo api.js quando refresh falha — força redirect ao login
  useEffect(() => {
    const handleForceLogout = () => {
      setState({ checking: false, allowed: false, redirectTo: '/login' });
    };
    window.addEventListener('istudo:logout', handleForceLogout);
    return () => window.removeEventListener('istudo:logout', handleForceLogout);
  }, []);

  useEffect(() => {
    let mounted = true;

    const hardLogout = () => {
      try {
        setToken(null);
        localStorage.removeItem('usuario');
      } catch {}
      if (mounted) {
        setState({ checking: false, allowed: false, redirectTo: '/login' });
      }
    };

    const denyToLogin = () => {
      if (mounted) {
        setState({ checking: false, allowed: false, redirectTo: '/login' });
      }
    };

    const allow = () => {
      if (mounted) {
        setState({ checking: false, allowed: true, redirectTo: null });
      }
    };

    const redirect = (to) => {
      if (mounted) {
        setState({ checking: false, allowed: false, redirectTo: to });
      }
    };

    const validateSessionAndRoute = async () => {
      try {
        const hasToken = !!localStorage.getItem('token');

        const fetchMe = async () => {
          const meResp = await api.get('/usuarios/me');
          return meResp?.data || null;
        };

        let me = null;

        if (hasToken) {
          me = await fetchMe();
        } else {
          // 🔄 Não tenho token, mas posso ter cookie rt → tenta refresh manual
          try {
            const { data } = await api.post('/auth/refresh');
            const newToken = data?.token;
            if (!newToken) throw new Error('refresh sem token');

            setToken(newToken);
            me = await fetchMe();
          } catch {
            // Sem token e sem refresh válido → vai pro login
            denyToLogin();
            return;
          }
        }

        if (!mounted) return;

        // Se /usuarios/me não retornou um usuário válido, nega
        const tipo = me?.tipo;
        if (!TIPOS.has(tipo)) {
          // sessão até pode existir, mas não atende o guard esperado → manda pro login
          hardLogout();
          return;
        }

        // mantém usuário atualizado no storage (ajuda telas que leem localStorage)
        try {
          localStorage.setItem('usuario', JSON.stringify(me || {}));
        } catch {}

        const dashboardCorreto = resolveDashboardPath(tipo);
        if (!dashboardCorreto) {
          hardLogout();
          return;
        }

        // 🔐 Regra: professor não acessa instituicao e vice-versa
        const wantsProfessor = currentPath.startsWith('/dashboard/professor');
        const wantsInstituicao = currentPath.startsWith('/dashboard/instituicao');

        if (wantsProfessor && tipo !== 'professor') {
          redirect(dashboardCorreto);
          return;
        }

        if (wantsInstituicao && tipo !== 'instituicao') {
          redirect(dashboardCorreto);
          return;
        }

        // Qualquer outra rota privada: sessão ok
        allow();
      } catch {
        // Erro inesperado → sessão inválida (evita estado zumbi)
        hardLogout();
      }
    };

    validateSessionAndRoute();

    return () => {
      mounted = false;
    };
  }, [currentPath]);

  if (state.checking) {
    return (
      <div className="pr-loading">
        <div className="pr-loading__card" role="status" aria-live="polite">
          <div className="pr-loading__spinner" aria-hidden="true" />
          <div className="pr-loading__text">
            <div className="pr-loading__title">Verificando acesso</div>
            <div className="pr-loading__sub">Aguarde um instante…</div>
          </div>
        </div>
      </div>
    );
  }

  if (state.redirectTo) {
    return <Navigate to={state.redirectTo} replace />;
  }

  if (!state.allowed) {
    // fallback defensivo (não deve cair aqui)
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}
